/**
 * The journal's doors into Boona Direct: the top-bar "Chats" button with the unread
 * badge, and "Write to client" on an appointment. Both render nothing unless the
 * module is on and the user has the inbox permission.
 */
import { useTranslation } from "react-i18next";
import { MessageCircle, MessagesSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { openInbox } from "@/lib/inbox/links";
import { useInboxEnabled, useInboxUnread } from "@/hooks/inbox/useInbox";

export function ChatsButton({ className }: { className?: string }) {
  const { t } = useTranslation();
  const enabled = useInboxEnabled();
  const { data } = useInboxUnread(enabled);
  if (!enabled) return null;
  const unread = data?.unread_threads ?? 0;
  return (
    <Button
      variant="ghost"
      className={cn("pill relative", className)}
      onClick={() => openInbox()}
      title={t("inbox.entry.chats_hint", "Boona Direct — every messenger in one tab")}
    >
      <MessagesSquare size={16} className="mr-1" />
      {t("inbox.entry.chats", "Chats")}
      {unread > 0 && (
        <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-semibold text-white">
          {unread > 9 ? "9+" : unread}
        </span>
      )}
    </Button>
  );
}

export function WriteToClientButton({ clientId, className }: { clientId: number | null | undefined; className?: string }) {
  const { t } = useTranslation();
  const enabled = useInboxEnabled();
  if (!enabled || !clientId) return null;
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={cn("h-8 gap-1.5 text-xs", className)}
      onClick={() => openInbox({ client: clientId })}
    >
      <MessageCircle className="h-3.5 w-3.5" />
      {t("inbox.entry.write", "Write to client")}
    </Button>
  );
}
